import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import PageSetup from 'bcl/components/page-setup';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import Modal from 'bcl/components/modal';
import UserEdit from 'bcl/components/user-edit';
import { Textarea } from '@ember/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { createExampleItems } from 'bcl/misc/createExampleItems';
import { createExampleOrders } from 'bcl/misc/createExampleOrders';
import { createExampleProductGroups } from 'bcl/misc/createExampleProductGroups';
import { createExampleLocations } from 'bcl/misc/createExampleLocation';
import { createExampleSuppliers } from 'bcl/misc/createExampleSuppliers';
import { createExampleUsers } from 'bcl/misc/createExampleUsers';
import { createExampleStocktakings } from 'bcl/misc/createExampleStocktakings';
import type SessionService from 'bcl/services/session';
import { version } from '~build/package';
import { abbreviatedSha } from '~build/git';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import type NotificationService from 'bcl/services/notification';
import type UserModel from 'bcl/models/user';
import type ProductGroupModel from 'bcl/models/product-group';

export default class AdminRoute extends Component {
  @service declare session: SessionService;
  @service declare store: Store;
  @service declare notification: NotificationService;
  @service declare router: RouterService;

  @tracked modalModel: UserModel | undefined = undefined;
  @tracked modalShown = false;
  @action
  closeModal() {
    this.modalShown = false;
  }

  @action
  createUser() {
    this.modalModel = this.store.createRecord('user', {
      email: '',
      role: 'user',
    });
    this.modalShown = true;
  }
  @action
  editUser(user) {
    this.modalModel = user;
    this.modalShown = true;
  }

  @tracked clientVersion = `${version} (${abbreviatedSha})`;

  @tracked to_export = '(noch keine Daten exportiert)';
  @action exportData() {
    const items = this.store.peekAll('item');
    console.log(items.length);
    const data = items.map((item) => {
      return {
        id: item.id,
        name: item.name,
        active: item.active,
        supplierItems: item.supplierItems.map((si) => {
          return {
            supplier_id: si.supplier.id,
            supplier: si.supplier?.name || 'unbekannt',
            articlenr: si.articlenr,
            price: si.price,
          };
        }),
      };
    });
    console.log(data);
    this.to_export = JSON.stringify(data, null, 2);
  }
  @action
  async createExampleModels() {
    // TODO nur 5 gleichzeitig anlegen, weil sonsten wieder probleme auftauchen

    await createExampleUsers(this.store);
    await createExampleProductGroups(this.store);
    await createExampleLocations(this.store);
    await createExampleSuppliers(this.store);
    await createExampleOrders(this.store);

    // bei items auch gleich location und supplier items erstellen
    await createExampleItems(this.store);
    await createExampleStocktakings(this.store);
    await this.session.loadSession();
  }
  @action
  async updateFirstPG() {
    const pg = this.store.peekRecord<ProductGroupModel>('product-group', 1);
    console.log(pg);
    if (pg) {
      pg.name = pg.name.split(':')[0] + ':' + Math.random();
      await pg.save();
    }
  }
  @action
  fehler() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    undefinedFunction();
  }

  <template>
    {{pageTitle "Admin"}}

    {{#if this.modalShown}}
      <Modal @size="big">
        <UserEdit @model={{this.modalModel}} @closeModal={{this.closeModal}} />
      </Modal>
    {{/if}}
    <PageSetup @title="Admin">
      <h1 class="text-xl mt-8">Export</h1>
      <div class="block mb-4">
        <button
          {{on "click" this.exportData}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >Daten exportieren</button>
        <Textarea class="w-full h-48 mt-4" @value={{this.export}} readonly />
      </div>
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">
            <h1
              class="text-base font-semibold text-gray-900 dark:text-white"
            >Nutzerverwaltung</h1>
            <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">Eine Liste
              aller Benutzer in Ihrem Konto, einschließlich ihres Namens, ihrer
              E-Mail und Rolle.</p>
          </div>
          <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              {{on "click" this.createUser}}
              type="button"
              class="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
            >Nutzer hinzufügen</button>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
            >
              <table
                class="relative min-w-full divide-y divide-gray-300 dark:divide-white/15"
              >
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white"
                    >Name</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >Email</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >Role</th>
                    <th scope="col" class="py-3.5 pr-4 pl-3 sm:pr-0">
                      <span class="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-white/10">
                  {{#each @model.users as |user|}}
                    <tr>
                      <td
                        class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-white"
                      >{{user.name}}</td>
                      <td
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
                      >{{user.email}}</td>
                      <td
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
                      >{{user.role}}</td>
                      <td
                        class="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0"
                      >
                        <button
                          type="button"
                          {{on "click" (fn this.editUser user)}}
                          href="#"
                          class="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >bearbeiten<span class="sr-only">,
                            {{user.name}}</span></button>
                      </td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <h1 class="text-xl mt-8">Versions</h1>
      <div class="block mb-4">
        Client:
        {{this.clientVersion}}<br />
        Server:
        {{@model.version}}
      </div>

      {{#if this.session.isSpruce}}
        <h2 class="text-lg font-semibold">Test Funktionen (nur @spruce)</h2>
        <button
          {{on "click" this.createExampleModels}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >create example models</button>
        <button
          {{on "click" this.updateFirstPG}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >Add Random String to Name of first PG</button>
        <button
          {{on "click" this.fehler}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >Fehler produzieren</button>
      {{/if}}
    </PageSetup>
  </template>
}
